/**
 * Likely valid signatures (Admin → Petition signers, the counts table's "Likely valid" column and its detail view):
 * a staff-only estimate per petition from GET /api/dashboard/admin/signers/validity (worker/src/petition-validity.js),
 * totals only. The registration-failure assumption is a staff setting that defaults to none; every summary link
 * carries it, so the numbers on screen and in the files match.
 *
 *   Column    high to low range, or the counts-only figure, with the basis (lines checked, counts only, incomplete)
 *   Detail    the stages (a)-(g), the range, the cushion over the minimum, lines not transcribed, the confidence note,
 *             what is not checked, the rules with citations, links to the signer map and the candidate's summary
 *   Exports   every candidate's summary on one printable page (or Markdown), and a CSV of the numbers
 */
import { useEffect, useState } from "react";

export interface Stage { key: string; label: string; value: number | null; removed?: number; applied: true | "range" | "not checked"; rule?: string; note?: string }
export interface Estimate {
  candidate: string; name: string; office: string; ward: number | null; district: number | null; race: string;
  minimum: number | null; minimumCite: string | null; countMinimum: number | null;
  lines: { onSheets: number | null; transcribed: number; notTranscribed: number | null; staged: number; fromOcr?: number; notGeocoded?: number; illegible?: number; unmatched?: number; overlap?: number | null };
  /** reviewed | unreviewed | mixed: whether lines from unreviewed transcription (OCR) are in it, and the label. */
  source?: "reviewed" | "unreviewed" | "mixed"; sourceLabel?: string | null;
  regAssumption: number; stages: Stage[]; high: number | null; low: number | null; cushion: { high: number | null; low: number | null };
  verdict: "above" | "below" | "straddles" | "unknown"; verdictBasis?: "counted" | "extrapolated"; basis: "lines" | "counts" | "pending"; complete: boolean;
  extrapolated: { high: number; low: number; coverage: number; label: string; cushion?: { high: number; low: number } } | null;
  confidence: "higher" | "medium" | "low"; confidenceNote: string; notChecked: string[];
}
export interface Rule { id: string; stage: string; scope: string; status: string; rule: string; cite: string; estimate: string; urls: string[] }
export interface ValidityPage {
  rows: Estimate[]; regAssumption: number; rules: Rule[]; disclaimer: string; researchedOn: string;
  totals: { petitions: number; above: number; below: number; straddles: number; unknown: number; incomplete: number; lines: number; counts: number; pending: number };
}

const API = "/api/dashboard/admin/signers";
const count = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("en-US"));
const plus = (n: number | null | undefined) => (n == null ? "—" : `${n >= 0 ? "+" : "−"}${Math.abs(n).toLocaleString("en-US")}`);
const regQuery = (reg: number) => (reg ? `reg=${reg}` : "");
/** A summary or export URL, with the assumption when one is set. */
export function summaryUrl(kind: "summary" | "summaries" | "validity.csv", opts: { candidate?: string; format?: string; reg: number }): string {
  const p = new URLSearchParams();
  if (opts.candidate) p.set("candidate", opts.candidate);
  if (opts.format) p.set("format", opts.format);
  if (opts.reg) p.set("reg", String(opts.reg));
  const s = p.toString();
  return `${API}/${kind}${s ? `?${s}` : ""}`;
}

/** The column's text: "1,950–2,010", a single figure when both ends agree, or "—" while pending. */
export function likelyText(e: Estimate | undefined): string {
  if (!e || e.high == null || e.low == null) return "—";
  return e.high === e.low ? count(e.high) : `${count(e.low)}–${count(e.high)}`;
}
/** The basis chip under the figure. */
export function basisText(e: Estimate): string {
  if (e.basis === "pending") return "Pending";
  if (e.basis === "counts") return "Counts only";
  return e.complete ? "Lines checked" : "Incomplete";
}
const VERDICT: Record<Estimate["verdict"], [string, string]> = {
  above: ["Above minimum", "chip--positive"], below: ["Below minimum", "chip--negative"], straddles: ["Minimum within range", "chip--neutral"], unknown: ["No minimum", "chip--neutral"],
};

/** Fetches the estimates for an assumption; null while loading, "failed" on an error. */
export function useValidity(reg: number): ValidityPage | null | "failed" {
  const [data, setData] = useState<ValidityPage | null | "failed">(null);
  useEffect(() => {
    let live = true;
    setData(null);
    fetch(`${API}/validity${reg ? `?${regQuery(reg)}` : ""}`, { credentials: "include", cache: "no-store" })
      .then((x) => (x.ok ? x.json() : Promise.reject())).then((d) => live && setData(d)).catch(() => live && setData("failed"));
    return () => { live = false; };
  }, [reg]);
  return data;
}

export function LikelyCell({ e, open, onOpen }: { e: Estimate | undefined; open: boolean; onOpen: () => void }) {
  if (!e) return <td>—</td>;
  return (
    <td>
      <button type="button" className="link-button" aria-expanded={open} onClick={onOpen}><b>{likelyText(e)}</b></button>
      {e.basis !== "pending" && <> <span className={`chip ${e.basis === "lines" && e.complete ? "chip--positive" : "chip--neutral"}`}>{basisText(e)}</span></>}
      {e.sourceLabel && <> <span className="chip chip--neutral" title="Machine-read lines, not yet checked by staff">{e.sourceLabel}</span></>}
    </td>
  );
}

/** The assumption and the exports, above the counts table. */
export function ValidityControls({ reg, setReg, data }: { reg: number; setReg: (n: number) => void; data: ValidityPage | null | "failed" }) {
  const [draft, setDraft] = useState(String(reg));
  const t = data && data !== "failed" ? data.totals : null;
  return (
    <div className="signers-validity" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
      <label>Registration-failure assumption{" "}
        <input type="number" min={0} max={60} step={1} value={draft} style={{ width: 64 }} aria-describedby="reg-note"
          onChange={(ev) => setDraft(ev.target.value)}
          onBlur={() => { const n = Math.max(0, Math.min(60, Math.round(Number(draft) || 0))); setDraft(String(n)); setReg(n); }} />%
      </label>
      <span id="reg-note" className="access-sub">An assumption, not a sourced rate (none was found). Default: none.</span>
      <a className="btn btn--secondary" href={summaryUrl("summaries", { format: "html", reg })} target="_blank" rel="noopener">Summaries for all candidates</a>
      <a className="btn btn--secondary" href={summaryUrl("summaries", { format: "md", reg })} download>Markdown</a>
      <a className="btn btn--secondary" href={summaryUrl("validity.csv", { reg })} download>Estimates CSV</a>
      {t && <span className="access-sub">{count(t.above)} above, {count(t.straddles)} within range, {count(t.below)} below, {count(t.incomplete)} incomplete</span>}
    </div>
  );
}

const STAGE_VALUE = (s: Stage) => (s.applied === "not checked" ? "Not checked" : s.applied === "range" ? `${count(s.value)} at the low end` : count(s.value));

/** One petition's detail: stages, range, cushion, coverage, confidence, what isn't checked, rules, links. */
export function ValidityDetail({ e, rules, reg, onClose }: { e: Estimate; rules: Rule[]; reg: number; onClose: () => void }) {
  const [label, cls] = VERDICT[e.verdict];
  const byId = new Map(rules.map((r) => [r.id, r]));
  const used = [...new Set(e.stages.map((s) => s.rule).filter((r): r is string => !!r)), "registered-at-address", "registration-failure-rate"];
  return (
    <section className="access-card signers-validity-detail" aria-label={`Likely valid signatures: ${e.name}`}>
      <header style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>{e.name} <span className="access-sub">{e.race}</span></h3>
        <span className={`chip ${cls}`}>{label}{e.verdictBasis === "extrapolated" ? " (extrapolated)" : ""}</span>
        <span className="chip chip--neutral">Confidence: {e.confidence}</span>
        {e.sourceLabel && <span className="chip chip--neutral">{e.sourceLabel}</span>}
        <button type="button" className="btn btn--secondary" onClick={onClose}>Close</button>
      </header>
      <p className="access-sub">Estimate, not a Board ruling. Totals only.</p>
      <div className="art-table-wrap"><table className="art-table">
        <tbody>
          {e.stages.map((s) => (
            <tr key={s.key}><th scope="row">({s.key}) {s.label}</th><td>{STAGE_VALUE(s)}</td><td>{s.removed ? `−${count(s.removed)}` : ""}</td><td className="access-sub">{s.note ?? ""}</td></tr>
          ))}
        </tbody>
        <tfoot>
          <tr><th scope="row">Likely valid</th><td colSpan={3}><b>{count(e.low)} to {count(e.high)}</b>{reg ? ` (low end less the ${reg}% assumption)` : ""}</td></tr>
          <tr><th scope="row">Minimum</th><td colSpan={3}>{count(e.minimum)} <span className="access-sub">{e.minimumCite ?? ""}</span></td></tr>
          <tr><th scope="row">Cushion</th><td colSpan={3}>{plus(e.cushion.low)} to {plus(e.cushion.high)}</td></tr>
          {e.extrapolated && <tr><th scope="row">Extrapolated</th><td colSpan={3}>{count(e.extrapolated.low)} to {count(e.extrapolated.high)} <span className="access-sub">{e.extrapolated.label}</span></td></tr>}
          <tr><th scope="row">Lines</th><td colSpan={3}>{count(e.lines.onSheets)} signed on the sheets, {count(e.lines.transcribed)} transcribed, {count(e.lines.notTranscribed)} not yet{e.lines.fromOcr ? `, ${count(e.lines.fromOcr)} of them from unreviewed transcription${e.lines.notGeocoded ? ` (${count(e.lines.notGeocoded)} not geocoded yet)` : ""}` : ""}{e.lines.staged ? `, ${count(e.lines.staged)} OCR lines staged (not reviewed, not counted)` : ""}</td></tr>
        </tfoot>
      </table></div>
      <p>{e.confidenceNote}</p>
      <h4>Not checked</h4>
      <ul>{e.notChecked.map((s) => <li key={s}>{s}</li>)}</ul>
      <h4>Rules applied</h4>
      <ul>{used.map((id) => byId.get(id)).filter((r): r is Rule => !!r).map((r) => (
        <li key={r.id}>{r.rule} <span className="access-sub">{r.cite}{r.status === "unverified" ? " (unverified: not used)" : ""} {r.urls.map((u) => <a key={u} href={u} target="_blank" rel="noopener noreferrer">[source]</a>)}</span></li>
      ))}</ul>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {e.basis === "lines" && <a className="btn btn--secondary" href={`/dashboard/admin/signers/map?candidate=${encodeURIComponent(e.candidate)}`}>Signer map</a>}
        <a className="btn btn--secondary" href={summaryUrl("summary", { candidate: e.candidate, format: "html", reg })} target="_blank" rel="noopener">Summary page</a>
        <a className="btn btn--secondary" href={summaryUrl("summary", { candidate: e.candidate, format: "md", reg })} download>Markdown</a>
        <a className="btn btn--secondary" href={summaryUrl("summary", { candidate: e.candidate, format: "csv", reg })} download>CSV</a>
      </div>
    </section>
  );
}
